// ─── EDUCATION (single source of truth) ───────────────────────────────────────
export const education = {
  degree: "Bachelor in Computer Science",
  degreeFull: "Bachelors of Computer Science",
  institute: "PUCIT",
  instituteFull: "Punjab University College of Information Technology",
  date: "2016 - 2020",
};

// Education entry shown on the Experience & Education timeline.
export const educationTimeline = {
  title: education.degree,
  company: education.institute,
  tenure: education.date,
  description: "",
  companyLink: "",
};

export const experiences = [
  {
    company: "Lua AI",
    companyLink: "https://www.linkedin.com/company/lua-ai/",
    position: "Senior Software Engineer",
    date: "Oct 2025 - Present",
    description:
      "Implementation lead building and running production AI agents for enterprise clients across insurance, fintech lending, healthcare, retail operations, ad analytics, and venture capital on an enterprise AI agent OS platform. Running WhatsApp claims agents in five countries, leading five concurrent client engagements while mentoring partner developers, and fixing platform internals (agent versioning, QA pipeline, scheduling, channel integrations) serving thousands of live agents.",
    fullstack: {
      details: [
        "Built production WhatsApp insurance claims agents across 5 countries (Kenya, Uganda, Pakistan, Nigeria, Ghana) on a NestJS/TypeScript runtime, running 24/7 in two markets and averaging 95%+ satisfaction on daily client QA audits",
        "Engineered the claims document pipeline (vision-based classification, SHA-256 plus semantic near-duplicate detection, URL-anchored extraction), eliminating whole classes of document-loss defects across all markets",
        "Lead 5 concurrent client engagements spanning micro-insurance, an SME-lending fintech (~23k conversations/month), a 260-store restaurant group, ad analytics, and a VC fund, mentoring 3 partner developers through scoping, delegation, and code review",
        "Root-caused and fixed platform-wide defects in the agent OS serving thousands of live agents: agent-versioning bugs, a silent 5-week QA-pipeline outage (evaluation prompts cut from ~213K to ~13.5K tokens), and WhatsApp/Slack channel failures",
        "Shipped features and fixes in the platform's React + Tauri desktop app and its published npm design system",
      ],
      skills: [
        "NestJS",
        "React",
        "TypeScript",
        "Node.js",
        "Jest",
        "MongoDB",
        "PostgreSQL",
        "Kubernetes",
        "LLM Integration",
      ],
    },
    frontend: {
      details: [
        "Built a React-based agent creation wizard that lets non-technical users configure and launch AI agents without engineering support",
        "Developed the analytics dashboard clients use to monitor production agent activity, in React and TypeScript",
        "Shipped features and fixes in the platform's React + Tauri desktop app and its published npm design system",
        "Contributed to skill platform UI enabling developers to publish and configure custom AI agent capabilities",
      ],
      skills: [
        "React",
        "TypeScript",
        "NestJS",
        "Jest",
        "MongoDB",
        "PostgreSQL",
        "Kubernetes",
        "Generative AI",
      ],
    },
    backend: {
      details: [
        "Designed and run production AI agent backends for WhatsApp insurance claims across 5 countries on a NestJS/TypeScript runtime, running 24/7 in two markets and averaging 95%+ satisfaction on daily client QA audits",
        "Engineered the claims document pipeline (vision-based classification, SHA-256 plus semantic near-duplicate detection, URL-anchored extraction), eliminating whole classes of document-loss defects across all markets",
        "Improved the internal TypeScript agent framework: cron scheduling with timezone support, webhook orchestration, and channel lifecycle management across thousands of production agents",
        "Root-caused and fixed platform-wide defects: agent-versioning bugs, a silent 5-week QA-pipeline outage (evaluation prompts cut from ~213K to ~13.5K tokens), and WhatsApp/Slack channel failures",
      ],
      skills: [
        "NestJS",
        "Node.js",
        "TypeScript",
        "Jest",
        "MongoDB",
        "PostgreSQL",
        "Kubernetes",
        "REST APIs",
        "LLM Integration",
      ],
    },
  },
  {
    company: "Burq",
    companyLink: "https://www.linkedin.com/company/burq/",
    position: "Senior Software Engineer",
    date: "Oct 2024 - Nov 2025",
    description:
      "Led full-stack development at Burq Inc, a last-mile delivery management platform on AWS. Migrated the frontend to TypeScript React, introduced frontend E2E testing from scratch with Cypress, built merchant-facing features including delivery incident management and driver fleet tools backed by AWS SNS/SQS event pipelines, and led an analytics microservice migration to Snowflake with DBT, onboarding 50+ new merchants and cutting analytics load times by 50%.",
    fullstack: {
      details: [
        "Migrated Burq's frontend to TypeScript React with shadcn (resolving 100% of UI inconsistency issues), introduced Cypress E2E testing from scratch, and sped up CI by replacing slow database flushes with collision-free test data per run.",
        "Built a full-stack delivery incidents system with React and Node.js, supporting merchants and cutting resolution time from more than 4 hours to less than 1 hour.",
        "Built driver-fleet management and route optimization features on AWS SNS/SQS event pipelines, enabling onboarding of 20+ new merchants to manage their driver fleet and saving more than 10 hours weekly in dispatch time.",
        "Led analytics feature, migrating PostgreSQL to Snowflake and modeling fact/dimension tables with DBT, cutting analytics load time by 50% through optimized queries and React.js rendering.",
      ],
      skills: [
        "React.js",
        "TypeScript",
        "shadcn/ui",
        "Node.js",
        "Jest",
        "Supertest",
        "Cypress",
        "PostgreSQL",
        "Snowflake",
        "AWS",
        "SQS",
        "SNS",
      ],
    },
    frontend: {
      details: [
        "Refactored Burq's frontend to TypeScript React with shadcn (100% UI consistency, zero type-related errors), introduced Cypress E2E testing from scratch, and cut CI time by replacing slow database flushes with collision-free test data.",
        "Built the driver management UI in React, enabling onboarding of 20+ new merchants to manage their driver fleet and saving more than 10 hours weekly in dispatch time.",
        "Led analytics feature, created comprehensive dashboards using React, providing real-time insights and reducing load times by 50% through optimized rendering and data fetching.",
      ],
      skills: [
        "React.js",
        "TypeScript",
        "shadcn/ui",
        "Jest",
        "Cypress",
        "PostgreSQL",
        "Snowflake",
      ],
    },
    backend: {
      details: [
        "Built a delivery incidents system, supporting merchants by automation and cutting resolution time from more than 4 hours to less than 1 hour.",
        "Built driver management and route optimization services on AWS SNS/SQS event pipelines, enabling onboarding of 20+ new merchants to manage their driver fleet and saving more than 10 hours weekly in dispatch time.",
        "Led analytics feature, cloning PostgreSQL to Snowflake with Airbyte, optimized queries through fact/dimension tables and cutting analytics load time by 50%.",
        "Sped up CI by removing slow database flushes in favor of collision-free test data per run, alongside Supertest integration coverage.",
      ],
      skills: [
        "Node.js",
        "TypeScript",
        "Jest",
        "Supertest",
        "PostgreSQL",
        "Snowflake",
        "DBT",
        "Airbyte",
        "AWS",
        "SQS",
        "SNS",
      ],
    },
  },
  {
    company: "Volocopter GmbH",
    companyLink: "https://www.linkedin.com/company/volocopter/",
    position: "Senior Software Engineer",
    date: "Dec 2023 - Sep 2024",
    description:
      "Contributed to VoloIQ, the cloud-based platform powering Volocopter's eVTOL ecosystem, across a Kafka-based microservices backend on Azure. Implemented TDD across 5 core services, reducing production bugs from 15+ to under 5 per release. Tuned Postgres queries and indexing for sub-200ms APIs, optimized frontend Lighthouse scores from 78 to 91, built 100+ E2E test flows saving 30 hours of QA monthly, and ran GitHub Actions CI/CD, Kubernetes deployments, and DevOps on-call rotations.",
    fullstack: {
      details: [
        "Implemented test-driven development (TDD) across 5 core microservices, reducing production bugs from an average of 15 to less than 5 per release cycle and improving system stability.",
        "Optimized front-end, improving Lighthouse performance score from 78 to 91 and reducing average page load time from above 3s to less than 2.5s.",
        "Designed and built end-to-end test suites covering over 100+ user flows, saving around 30 hours of manual QA effort per month.",
        "Expanded into DevOps, running GitHub Actions CI/CD pipelines, Kubernetes deployments on Azure, and on-call rotations, resolving 100+ infrastructure issues across production environments.",
      ],
      skills: [
        "React.js",
        "TypeScript",
        "Cypress",
        "Chakra UI",
        "Storybook",
        "Node.js",
        "Jest",
        "Python",
        "Kafka",
        "Socket.IO",
        "PostgreSQL",
        "Pact Tests",
        "CI/CD",
        "Kubernetes",
        "Azure",
      ],
    },
    frontend: {
      details: [
        "Optimized front-end, improving Lighthouse performance score from 78 to 91 and reducing average page load time from above 3s to less than 2.5s.",
        "Designed and built end-to-end test suites for frontend components, covering over 100+ user flows and saving around 30 hours of manual QA effort per month.",
        "Shipped React.js and TypeScript features across 5 core VoloIQ modules, backing each with unit and E2E tests as part of the TDD push that cut production bugs from 15+ to under 5 per release.",
        "Implemented a responsive design library using Chakra UI and Storybook, standardizing UI patterns across 5 core micro frontends.",
      ],
      skills: [
        "React.js",
        "TypeScript",
        "Cypress",
        "Chakra UI",
        "Storybook",
        "Jest",
        "Unit Testing",
        "E2E Testing",
      ],
    },
    backend: {
      details: [
        "Implemented test-driven development (TDD) for backend microservices, reducing production bugs from an average of 15 to less than 5 per release cycle across 5 core services.",
        "Optimized backend APIs with Node.js and Postgres, reducing response times under 200ms for mutations and under 80ms for reads through query tuning and database indexing.",
        "Integrated Kafka with the Saga pattern for real-time data processing in the VoloIQ platform, enhancing system reliability for 5 core modules.",
        "Expanded into DevOps, running GitHub Actions CI/CD pipelines, Kubernetes deployments on Azure, and on-call rotations, resolving 100+ infrastructure issues across production environments.",
      ],
      skills: [
        "Node.js",
        "Python",
        "Kafka",
        "PostgreSQL",
        "Jest",
        "Pact Tests",
        "CI/CD",
        "Kubernetes",
        "Azure",
      ],
    },
  },
  {
    company: "AHOM Limited",
    companyLink: "https://www.linkedin.com/company/ahom-limited/",
    position: "Technical Team Lead",
    date: "Apr 2024 - May 2025",
    note: "Part-time",
    description:
      "Led full-stack development and system architecture of a financial platform from the ground up. Built a React design system with 30+ reusable components, architected Python and Node.js microservices with Kafka for event-driven services, hardened security with anti-CSRF tokens, dynamic token expiry, and multiple social logins, developed a KYC system with facial recognition and OCR on open-source DeepFace and PaddleOCR, and containerized all services with Docker, achieving 95% test coverage across the stack.",
    fullstack: {
      details: [
        "Led frontend development and overall system design, building the system UI and design library from scratch with React and optimized Webpack settings, creating 30+ reusable components with Cypress test coverage.",
        "Built microservices from scratch with Python and Node.js, using Kafka for event-driven communication across services and containerizing with Docker, achieving 95% unit and integration test coverage.",
        "Developed a Python-based KYC system with facial recognition, OCR, and liveness detection, built on open-source DeepFace and PaddleOCR instead of paid KYC vendors, keeping all identity data processing in-house for privacy compliance.",
        "Hardened platform security with anti-CSRF tokens, dynamic token expiry, and multiple social login (OAuth) integrations.",
      ],
      skills: [
        "React.js",
        "Node.js",
        "Python",
        "TypeScript",
        "Jest",
        "Cypress",
        "MongoDB",
        "PostgreSQL",
        "Redis",
        "Kafka",
        "Docker",
      ],
    },
    frontend: {
      details: [
        "Led frontend development and UI architecture, building the system UI and design library from scratch with React and optimized Webpack settings.",
        "Created 30+ reusable components and implemented Cypress testing for test coverage.",
        "Built secure authentication flows with multiple social logins and dynamic session handling.",
        "Optimized client-side performance by integrating lazy loading and code splitting, reducing initial load times across 3+ major projects.",
      ],
      skills: ["React.js", "TypeScript", "Jest", "Redis"],
    },
    backend: {
      details: [
        "Architected and built microservices from scratch with Python and Node.js, using Kafka for event-driven communication across services, achieving 95% unit and integration test coverage.",
        "Developed a Python-based KYC system with facial recognition, OCR, and liveness detection, built on open-source DeepFace and PaddleOCR instead of paid KYC vendors, keeping all identity data processing in-house for privacy compliance.",
        "Hardened API security with anti-CSRF tokens, dynamic token expiry, and multiple social login (OAuth) integrations.",
        "Containerized all system services using Docker, cutting setup errors and streamlining deployments across 3+ projects.",
      ],
      skills: [
        "Node.js",
        "Python",
        "Kafka",
        "MongoDB",
        "PostgreSQL",
        "Redis",
        "Docker",
        "OAuth",
      ],
    },
  },
  {
    company: "BrainX Technologies",
    companyLink: "https://www.linkedin.com/company/brainx-technologies/",
    position: "Senior Software Engineer",
    date: "Nov 2021 - Dec 2023",
    description:
      "Led and mentored cross-functional teams of 4-6 engineers across 10+ client projects, most built from scratch, with a 95% on-time delivery rate. Improved delivery cycles from 4 to 2.5 weeks via agile process improvements, shipped real-time features with WebSockets, built a reusable React admin template adopted across 10+ projects, and reduced average deployment time from 4 hours to 1 hour.",
    fullstack: {
      details: [
        "Led and mentored cross-functional teams of 4-6 engineers, improving project delivery cycles from 4 weeks to 2.5 weeks via agile process enhancements.",
        "Delivered 10+ client projects, most built from scratch, with a 95% on-time delivery rate, including real-time features using WebSockets.",
        "Led deployments and infrastructure setup, created tutorials and documentation reducing average deployment time from 4 hours to 1 hour across releases.",
        "Developed an admin portal template with reusable React components, adopted across 10+ client projects so each new project started from a working portal instead of building UI from scratch.",
      ],
      skills: [
        "React.js",
        "Node.js",
        "Express.js",
        "MongoDB",
        "Socket.IO",
        "AWS",
        "GCP",
        "GraphQL",
        "Firebase",
      ],
    },
    frontend: {
      details: [
        "Led and mentored teams of 4-6 engineers, improving project delivery cycles from 4 weeks to 2.5 weeks via agile process enhancements.",
        "Led frontend development for 10+ client projects, most built from scratch, achieving a 95% on-time delivery rate with consistent responsive design standards.",
        "Developed an admin portal template with reusable React components, adopted across 10+ client projects so each new project started from a working portal instead of building UI from scratch.",
      ],
      skills: ["React.js", "TypeScript", "Jest", "AWS", "GCP"],
    },
    backend: {
      details: [
        "Led and mentored teams of 4-6 engineers, improving project delivery cycles from 4 weeks to 2.5 weeks via agile process enhancements.",
        "Delivered 10+ client projects, most built from scratch, with a 95% on-time delivery rate, including real-time features using WebSockets.",
        "Led deployments and infrastructure setup, created tutorials and documentation reducing average deployment time from 4 hours to 1 hour across releases.",
      ],
      skills: [
        "Node.js",
        "Express.js",
        "MongoDB",
        "Socket.IO",
        "AWS",
        "GCP",
        "GraphQL",
      ],
    },
  },
  {
    company: "Focusteck",
    companyLink: "https://www.linkedin.com/company/focusteck/",
    position: "Software Engineer",
    date: "Aug 2020 - Oct 2021",
    description:
      "Independently managed full life-cycle development of 3 client projects using Angular and NestJS, resulting in contract extensions. Built workflow automation features and a video pipeline that transcoded uploads to H.264 with FFmpeg and stored and streamed them via AWS S3 (first hands-on AWS experience), processing 50+ videos daily, and handled database design with MySQL and PostgreSQL.",
    fullstack: {
      details: [
        "Independently managed full life-cycle development of 3 client projects, resulting in clients extending contracts.",
        "Built a video pipeline that transcoded uploads to H.264 with FFmpeg and stored and streamed them via AWS S3 (first hands-on AWS experience), processing 50+ videos daily.",
        "Built a full-stack tree-based workflow automation feature using Angular, Nest.js, and MongoDB, letting users chain tasks into multi-step automated workflows.",
      ],
      skills: [
        "Angular",
        "NestJS",
        "PostgreSQL",
        "AWS S3",
        "Firebase",
        "Material UI",
        "Socket.IO",
      ],
    },
    frontend: {
      details: [
        "Independently managed development of 3 client projects, resulting in clients extending contracts.",
        "Built in-browser video playback and streaming, backed by an FFmpeg H.264 transcode pipeline and AWS S3, handling 50+ videos daily.",
        "Built a tree-based workflow automation UI in Angular, letting users chain tasks into multi-step automated workflows.",
      ],
      skills: ["Angular", "Material UI", "Socket.IO"],
    },
    backend: {
      details: [
        "Independently managed development of 3 client projects, resulting in clients extending contracts.",
        "Built a video pipeline transcoding uploads to H.264 with FFmpeg and storing and streaming them via AWS S3 (first hands-on AWS experience), processing 50+ videos daily.",
        "Built a tree-based workflow automation feature using Nest.js and MongoDB, letting users chain tasks into multi-step automated workflows.",
      ],
      skills: ["Nest.js", "Postgres", "AWS S3", "Firebase", "Socket.IO"],
    },
  },
];

// ─── RESUME DATA (derived from `experiences`) ─────────────────────────────────
// Each resume flavour flattens one variant onto the shared company/date/position
// so the resume components keep receiving { company, date, position, details,
// skills }. Update `experiences` above and every resume follows automatically.
const resumeVariant = (variant) =>
  experiences.map(({ company, date, position, [variant]: entry }) => ({
    company,
    date,
    position,
    details: entry.details,
    skills: entry.skills,
  }));

export const experienceData = resumeVariant("fullstack");
export const frontendExperienceData = resumeVariant("frontend");
export const backendExperienceData = resumeVariant("backend");
